import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLocale, getCurrencySymbol } from "../lang/useLanguage";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const fmtDt = (d) => d ? new Date(d).toLocaleString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// ─── VARYANS ANALİZİ SEKMESİ ─────────────────────────────────────────────
const VarianceAnalysisTab = ({ t, themeName }) => {
  const { t: varianceLang, lang: varianceCurrentLang } = useLanguage();
  const vr = varianceLang.reports || {};
  const varianceLocale =
    varianceCurrentLang === "en" ? "en-US" : "tr-TR";

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)).toISOString().slice(0,10));
  const [endDate, setEndDate]     = useState(new Date().toISOString().slice(0,10));
  const [productCode, setProductCode] = useState("");

  const moneyV = v => `${Number(v||0).toLocaleString(varianceLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${getCurrencySymbol()}`;

  const fetchVariance = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/variance-analysis?start_date=${startDate}&end_date=${endDate}`;
      if (productCode) url += `&product_code=${productCode}`;
      const res = await fetch(url, { headers: headers() });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [startDate, endDate, productCode]);

  useEffect(() => {
    fetchVariance();
  }, [fetchVariance]);

  const exportExcel = () => {
    if (!data?.rows?.length) return;
    const hdrs = [
      vr.varianceOrderNo,
      vr.varianceProductCode,
      vr.varianceStandardCost,
      vr.varianceActualCost,
      vr.varianceValue,
      vr.variancePercent,
      vr.varianceStatus
    ];
    const rows = data.rows.map(r => [
      r.order_no, r.product_code,
      r.std_total_cost, r.actual_total_cost,
      r.total_variance, r.variance_pct,
      r.total_variance >= 0 ? vr.varianceSaving : vr.varianceOverrun
    ]);
    const csv = [hdrs, ...rows].map(r => r.map(v => `"${String(v||"").replace(/"/g,'""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `varyans_analizi_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:20 }}>
        <div style={{ fontSize:13, fontWeight:700, color:t.text.primary, marginBottom:14 }}>{vr.varianceFilters}</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr auto auto", gap:12, alignItems:"flex-end" }}>
          <div>
            <div style={{ fontSize:11, color:t.text.muted, marginBottom:6 }}>{vr.varianceStartDate}</div>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={{ width:"100%", padding:"10px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" }} />
          </div>
          <div>
            <div style={{ fontSize:11, color:t.text.muted, marginBottom:6 }}>{vr.varianceEndDate}</div>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              style={{ width:"100%", padding:"10px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" }} />
          </div>
          <div>
            <div style={{ fontSize:11, color:t.text.muted, marginBottom:6 }}>{vr.varianceProductCodeOptional}</div>
            <input type="text" value={productCode} onChange={e => setProductCode(e.target.value)}
              placeholder={vr.varianceAll}
              style={{ width:"100%", padding:"10px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" }} />
          </div>
          <button onClick={fetchVariance} style={{ padding:"10px 20px", borderRadius:8, border:"none", background:"linear-gradient(135deg,#3b82f6,#6366f1)", color:"#fff", cursor:"pointer", fontSize:13, fontWeight:600, whiteSpace:"nowrap" }}>
            {vr.varianceAnalyze}
          </button>
          <button onClick={exportExcel} style={{ padding:"10px 16px", borderRadius:8, border:`1px solid ${t.border.input}`, background:t.bg.toggle, color:t.text.secondary, cursor:"pointer", fontSize:13, whiteSpace:"nowrap" }}>
            📥 Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:t.text.muted }}>{vr.varianceLoading}</div>
      ) : !data ? null : (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
            {[
              { label:vr.varianceTotalWorkOrders, value: data.summary.total_orders, color:t.accent.blue, icon:"📋", isNum:true },
              { label:vr.varianceStandardCost, value: moneyV(data.summary.total_std_cost), color:t.text.secondary, icon:"📊" },
              { label:vr.varianceActualCost, value: moneyV(data.summary.total_actual_cost), color: data.summary.total_actual_cost > data.summary.total_std_cost ? t.accent.red : t.accent.green, icon:"💸" },
              { label:vr.varianceTotalVariance, value: (data.summary.total_variance >= 0 ? "+" : "") + moneyV(data.summary.total_variance), color: data.summary.total_variance >= 0 ? t.accent.green : t.accent.red, icon: data.summary.total_variance >= 0 ? "✅" : "⚠️" },
            ].map(card => (
              <div key={card.label} style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:"16px 20px", textAlign:"center", boxShadow:t.shadow.card }}>
                <div style={{ fontSize:20, marginBottom:6 }}>{card.icon}</div>
                <div style={{ fontSize:11, color:t.text.muted, textTransform:"uppercase", marginBottom:6 }}>{card.label}</div>
                <div style={{ fontSize:18, fontWeight:800, color:card.color }}>{card.value}</div>
              </div>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div style={{ background:"rgba(34,197,94,0.06)", border:"1px solid rgba(34,197,94,0.2)", borderRadius:14, padding:16, textAlign:"center" }}>
              <div style={{ fontSize:24, marginBottom:4 }}>✅</div>
              <div style={{ fontSize:13, color:"#22c55e", fontWeight:700, marginBottom:4 }}>{vr.varianceSavingProvided}</div>
              <div style={{ fontSize:28, fontWeight:900, color:"#22c55e" }}>{data.summary.favorable_count}</div>
              <div style={{ fontSize:11, color:t.text.muted }}>{vr.varianceWorkOrderUnit}</div>
            </div>
            <div style={{ background:"rgba(239,68,68,0.06)", border:"1px solid rgba(239,68,68,0.2)", borderRadius:14, padding:16, textAlign:"center" }}>
              <div style={{ fontSize:24, marginBottom:4 }}>⚠️</div>
              <div style={{ fontSize:13, color:"#ef4444", fontWeight:700, marginBottom:4 }}>{vr.varianceBudgetOverrun}</div>
              <div style={{ fontSize:28, fontWeight:900, color:"#ef4444" }}>{data.summary.unfavorable_count}</div>
              <div style={{ fontSize:11, color:t.text.muted }}>{vr.varianceWorkOrderUnit}</div>
            </div>
          </div>

          {Number(data.summary.actual_cost_entered_count || 0) === 0 && (
            <div style={{
              textAlign:"center",
              padding:"14px 18px",
              marginBottom:14,
              color:t.text.muted,
              background:t.bg.card,
              border:`1px solid ${t.border.subtle}`,
              borderRadius:14
            }}>
              {vr.varianceNoActualCostOrders}
            </div>
          )}

          {data.rows.length > 0 && (
            <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
              <div style={{ padding:"14px 20px", borderBottom:`1px solid ${t.border.default}`, fontSize:13, fontWeight:700, color:t.text.primary }}>
                📋 {vr.varianceWorkOrderDetail}
              </div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{ background:t.bg.theadRow }}>
                      {[
                        vr.varianceOrderNo,
                        vr.varianceProduct,
                        vr.varianceQuantity,
                        vr.varianceStandard,
                        vr.varianceActual,
                        vr.varianceValue,
                        vr.variancePercent,
                        vr.varianceStatus
                      ].map(h => (
                        <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:10, fontWeight:700, color:t.text.label, textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => {
                      const varianceValue = parseFloat(r.total_variance || 0);
                      const isPositive = varianceValue > 0.01;
                      const isNeutral = Math.abs(varianceValue) < 0.01;
                      const varColor = isNeutral ? t.text.muted : (isPositive ? t.accent.green : t.accent.red);
                      return (
                        <tr key={i} style={{ borderTop:`1px solid ${t.border.subtle}`, background: i%2===0 ? t.bg.rowAlt : t.bg.row }}>
                          <td style={{ padding:"10px 14px", fontSize:12, fontWeight:700, color:t.accent.blue }}>{r.order_no}</td>
                          <td style={{ padding:"10px 14px", fontSize:12, color:t.text.secondary }}>{r.product_code}</td>
                          <td style={{ padding:"10px 14px", fontSize:12, color:t.text.secondary }}>{r.quantity}</td>
                          <td style={{ padding:"10px 14px", fontSize:12, color:t.text.secondary }}>{moneyV(r.std_total_cost)}</td>
                          <td style={{ padding:"10px 14px", fontSize:12, color: parseFloat(r.actual_total_cost||0) > parseFloat(r.std_total_cost||0) ? t.accent.red : t.text.secondary }}>
                            {parseFloat(r.actual_total_cost||0) > 0 ? moneyV(r.actual_total_cost) : <span style={{color:t.text.muted}}>{vr.varianceNotEntered}</span>}
                          </td>
                          <td style={{ padding:"10px 14px", fontSize:12, fontWeight:700, color:varColor }}>
                            {parseFloat(r.actual_total_cost||0) > 0 ? (isPositive ? "+" : "") + moneyV(r.total_variance) : "—"}
                          </td>
                          <td style={{ padding:"10px 14px", fontSize:12, fontWeight:700, color:varColor }}>
                            {parseFloat(r.actual_total_cost||0) > 0 ? `${isPositive ? "+" : ""}${r.variance_pct}%` : "—"}
                          </td>
                          <td style={{ padding:"10px 14px" }}>
                            {parseFloat(r.actual_total_cost||0) > 0 ? (
                              <span style={{ fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20,
                                color:varColor, background:`${varColor}18`, border:`1px solid ${varColor}33` }}>
                                {isNeutral
                                  ? `— ${vr.varianceNeutral}`
                                  : isPositive
                                    ? `✓ ${vr.varianceSaving}`
                                    : `✗ ${vr.varianceOverrun}`}
                              </span>
                            ) : (
                              <span style={{ fontSize:11, color:t.text.muted }}>{vr.varianceNoData}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── RAPOR TİPLERİ ────────────────────────────────────────────────────────
const REPORT_TYPES = [
  { id: "production", labelKey: "reportTypeProduction", descKey: "reportTypeProductionDesc", icon: "🏭", color: "#60a5fa", endpoint: "/reports/production" },
  { id: "quality", labelKey: "reportTypeQuality", descKey: "reportTypeQualityDesc", icon: "✅", color: "#00ff9d", endpoint: "/reports/quality" },
  { id: "fire", labelKey: "reportTypeFire", descKey: "reportTypeFireDesc", icon: "🔥", color: "#f97316", endpoint: "/reports/fire" },
  { id: "maintenance", labelKey: "reportTypeMaintenance", descKey: "reportTypeMaintenanceDesc", icon: "🔧", color: "#fbbf24", endpoint: "/reports/maintenance" },
  { id: "energy", labelKey: "reportTypeEnergy", descKey: "reportTypeEnergyDesc", icon: "⚡", color: "#a78bfa", endpoint: "/reports/energy" },
  { id: "stock", labelKey: "reportTypeStock", descKey: "reportTypeStockDesc", icon: "📦", color: "#f87171", endpoint: "/reports/stock" },
  { id: "financial", labelKey: "reportTypeFinancial", descKey: "reportTypeFinancialDesc", icon: "💰", color: "#34d399", endpoint: "/reports/financial" },
  { id: "hr", labelKey: "reportTypeHr", descKey: "reportTypeHrDesc", icon: "👥", color: "#fb923c", endpoint: "/reports/hr" },
  { id: "kaizen", labelKey: "reportTypeKaizen", descKey: "reportTypeKaizenDesc", icon: "🔄", color: "#e879f9", endpoint: "/reports/kaizen" },
  { id: "ncr", labelKey: "reportTypeNcr", descKey: "reportTypeNcrDesc", icon: "🔴", color: "#ef4444", endpoint: "/reports/ncr" },
  { id: "sales", labelKey: "reportTypeSales", descKey: "reportTypeSalesDesc", icon: "🛒", color: "#22c55e", endpoint: "/reports/sales" },
  { id: "sales_personnel", labelKey: "reportTypeSalesPersonnel", descKey: "reportTypeSalesPersonnelDesc", icon: "💼", color: "#a78bfa", endpoint: "/reports/sales_personnel" },
  { id: "personnel_all", labelKey: "reportTypePersonnelAll", descKey: "reportTypePersonnelAllDesc", icon: "👥", color: "#fb923c", endpoint: "/reports/personnel_all" },
];

// ─── ÖZET KART ───────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, unit, icon, color, change, t }) => (
  <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: color || t.accent.blue }}>
          {value ?? "—"}
          <span style={{ fontSize: 13, fontWeight: 500, color: t.text.muted, marginLeft: 4 }}>{unit}</span>
        </div>
        {change != null && (
          <div style={{ fontSize: 11, color: change >= 0 ? t.accent.green : t.accent.red, marginTop: 4, fontWeight: 600 }}>
            {change >= 0 ? "▲" : "▼"} {Math.abs(change)}% geçen aya göre
          </div>
        )}
      </div>
      <span style={{ fontSize: 26, opacity: 0.7 }}>{icon}</span>
    </div>
  </div>
);

// ─── SATIŞ PERFORMANSI ───────────────────────────────────────────────────
const SalesPerformance = ({ t, themeName }) => {
  const { t: l } = useLanguage();
  const r = l.reports || {};
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [period, setPeriod]           = useState("half_year");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personDetail, setPersonDetail]     = useState(null);

  const moneyS = v => `${Number(v||0).toLocaleString(r.numberLocale || "tr-TR")} ${getCurrencySymbol()}`;
  const COLORS  = ["#60a5fa","#00ff9d","#f97316","#a78bfa","#f87171","#34d399","#fbbf24","#e879f9"];

  const fetchData = async (p) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/sales/performance?period=${p}`, { headers: headers() });
      if (r.ok) setData(await r.json());
    } catch {}
    setLoading(false);
  };

  const fetchPerson = async (person) => {
    try {
      const r = await fetch(`${API}/sales/performance/${encodeURIComponent(person)}`, { headers: headers() });
      if (r.ok) { setPersonDetail(await r.json()); setSelectedPerson(person); }
    } catch {}
  };

  useEffect(() => { fetchData(period); }, [period]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.text.muted }}>{r.salesPerfLoading}</div>;
  if (!data)   return <div style={{ padding: 40, textAlign: "center", color: t.text.muted }}>{r.salesPerfNoData}</div>;

  // Bölge karşılaştırma
  const domRow = (data.region_comparison||[]).find(r => r.region === "domestic") || {};
  const forRow = (data.region_comparison||[]).find(r => r.region === "foreign")  || {};

  // Personel listesini sırala
  const sortedPersonnel = [...(data.personnel||[])].sort((a,b) => parseFloat(b.total_amount||0) - parseFloat(a.total_amount||0));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Başlık + Dönem seçici */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: t.text.primary }}>{r.salesPerfTitle}</div>
        <div style={{ display: "flex", gap: 4, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 10, padding: 4 }}>
          {[["month",r.salesPeriodMonth],["quarter",r.salesPeriodQuarter],["half_year",r.salesPeriodHalfYear],["year",r.salesPeriodYear]].map(([v,label]) => (
            <button key={v} onClick={() => setPeriod(v)} style={{
              padding: "7px 14px", borderRadius: 7, border: "none", cursor: "pointer",
              background: period === v ? (themeName === "dark" ? "rgba(255,255,255,0.12)" : "#fff") : "none",
              color: period === v ? t.text.primary : t.text.muted,
              fontSize: 12, fontWeight: 600, boxShadow: period === v ? t.shadow.card : "none"
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* ── GENEL ÖZET KARTLAR ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14 }}>
        {[
          { label:r.salesTotalOrders,   value: data.summary?.total_orders||0,     unit:r.salesUnitOrders,    color:"#60a5fa" },
          { label:r.salesTotalRevenue,      value: moneyS(data.summary?.total_amount), unit:"",        color:"#00ff9d" },
          { label:r.salesPersonnel,  value: data.summary?.total_personnel||0,   unit:r.salesUnitPersonnel,    color:"#a78bfa" },
          { label:r.salesActiveCustomers,    value: data.summary?.total_customers||0,   unit:r.salesUnitCustomers, color:"#f97316" },
        ].map(card => (
          <div key={card.label} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card }}>
            <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>{card.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: card.color }}>{card.value} <span style={{ fontSize: 13, color: t.text.muted }}>{card.unit}</span></div>
          </div>
        ))}
      </div>

      {/* ── YURT İÇİ / YURT DIŞI KARŞILAŞTIRMA ── */}
      <div style={{ display: "grid", gridTemplateColumns: `${data.user_region === "all" ? "1fr 1fr" : "1fr"}`, gap: 14 }}>

        {/* Yurt İçi */}
        {(data.user_region === "all" || data.user_region === "domestic") && (
        <div style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 14, padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 24 }}>🏠</span>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#10b981" }}>{r.salesDomesticTitle}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
            {[
              { label:r.salesTotalRevenue,    value: moneyS(domRow.total_amount),    color:"#10b981" },
              { label:r.salesOrderLabel,        value: domRow.total_orders||0,         color:"#60a5fa" },
              { label:r.salesCustomerLabel,        value: domRow.unique_customers||0,     color:"#f97316" },
            ].map(item => (
              <div key={item.label} style={{ background: "rgba(0,0,0,0.2)", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: t.text.muted, textTransform: "uppercase", marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>
          {/* Yurt içi personel listesi */}
          <div style={{ fontSize: 11, fontWeight: 700, color: t.text.muted, textTransform: "uppercase", marginBottom: 8 }}>{r.salesPersonnelLabel}</div>
          {sortedPersonnel.filter(p => p.sales_region === "domestic" || (!p.sales_region && p.domestic_amount > 0)).map((p, i) => {
            const target = parseFloat(p.monthly_target||0);
            const actual = parseFloat(p.total_amount||0);
            const pct    = target > 0 ? Math.min(100, Math.round(actual/target*100)) : 0;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid rgba(16,185,129,0.15)` }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${COLORS[i%COLORS.length]}22`, border: `1px solid ${COLORS[i%COLORS.length]}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: COLORS[i%COLORS.length], flexShrink: 0 }}>
                  {(p.sales_rep||"?")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.primary }}>{p.sales_rep||"—"}</div>
                  <div style={{ fontSize: 10, color: t.text.muted }}>{p.sales_level||"rep"} · {p.total_orders} {r.salesOrderSuffix}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#10b981" }}>{moneyS(p.total_amount)}</div>
                  {target > 0 && <div style={{ fontSize: 10, color: pct >= 100 ? "#22c55e" : pct >= 70 ? "#fbbf24" : "#ef4444" }}>{r.salesTarget} %{pct}</div>}
                </div>
                <button onClick={() => fetchPerson(p.sales_rep)} style={{ padding: "3px 8px", borderRadius: 5, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.muted, cursor: "pointer", fontSize: 10 }}>{r.salesDetail}</button>
              </div>
            );
          })}
        </div>
        )}

        {/* Yurt Dışı */}
        {(data.user_region === "all" || data.user_region === "foreign") && (
        <div style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.25)", borderRadius: 14, padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 24 }}>✈️</span>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#8b5cf6" }}>{r.salesForeignTitle}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
            {[
              { label:r.salesTotalRevenue,    value: moneyS(forRow.total_amount),    color:"#8b5cf6" },
              { label:r.salesOrderLabel,        value: forRow.total_orders||0,         color:"#60a5fa" },
              { label:r.salesCustomerLabel,        value: forRow.unique_customers||0,     color:"#f97316" },
            ].map(item => (
              <div key={item.label} style={{ background: "rgba(0,0,0,0.2)", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: t.text.muted, textTransform: "uppercase", marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.text.muted, textTransform: "uppercase", marginBottom: 8 }}>{r.salesPersonnelLabel}</div>
          {sortedPersonnel.filter(p => p.sales_region === "foreign" || (!p.sales_region && p.foreign_amount > 0)).map((p, i) => {
            const target = parseFloat(p.monthly_target||0);
            const actual = parseFloat(p.total_amount||0);
            const pct    = target > 0 ? Math.min(100, Math.round(actual/target*100)) : 0;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid rgba(139,92,246,0.15)` }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: `${COLORS[(i+3)%COLORS.length]}22`, border: `1px solid ${COLORS[(i+3)%COLORS.length]}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: COLORS[(i+3)%COLORS.length], flexShrink: 0 }}>
                  {(p.sales_rep||"?")[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.primary }}>{p.sales_rep||"—"}</div>
                  <div style={{ fontSize: 10, color: t.text.muted }}>{p.sales_level||"rep"} · {p.total_orders} {r.salesOrderSuffix}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#8b5cf6" }}>{moneyS(p.total_amount)}</div>
                  {target > 0 && <div style={{ fontSize: 10, color: pct >= 100 ? "#22c55e" : pct >= 70 ? "#fbbf24" : "#ef4444" }}>{r.salesTarget} %{pct}</div>}
                </div>
                <button onClick={() => fetchPerson(p.sales_rep)} style={{ padding: "3px 8px", borderRadius: 5, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.muted, cursor: "pointer", fontSize: 10 }}>{r.salesDetail}</button>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* ── TÜM PERSONEL DETAYLI TABLO ── */}
      <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, fontSize: 14, fontWeight: 700, color: t.text.primary }}>
          {r.salesPersonnelDetail}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: t.bg.theadRow }}>
              {[r.salesColPersonnel,r.salesColRegion,r.salesColLevel,r.salesColOrders,r.salesColCompleted,r.salesColRevenue,r.salesColAvgOrder,r.salesColCustomer,r.salesColTarget,r.salesColDetail].map(h => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedPersonnel.length === 0 ? (
              <tr><td colSpan={10} style={{ padding: 40, textAlign: "center", color: t.text.placeholder }}>{r.salesPerfNoData}</td></tr>
            ) : sortedPersonnel.map((p, i) => {
              const target   = parseFloat(p.monthly_target||0);
              const actual   = parseFloat(p.total_amount||0);
              const pct      = target > 0 ? Math.min(100, Math.round(actual/target*100)) : null;
              const pctColor = pct === null ? t.text.muted : pct >= 100 ? "#22c55e" : pct >= 70 ? "#fbbf24" : "#ef4444";
              const regionColor = p.sales_region === "domestic" ? "#10b981" : p.sales_region === "foreign" ? "#8b5cf6" : t.text.muted;
              const regionLabel = p.sales_region === "domestic" ? `🏠 ${r.salesDomestic}` : p.sales_region === "foreign" ? `✈️ ${r.salesForeign}` : `🌐 ${r.salesAll}`;
              const levelLabels = {
                director: r.salesLevelDirector,
                manager: r.salesLevelManager,
                chief: r.salesLevelChief,
                rep: r.salesLevelRep,
              };
              return (
                <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i%2===0 ? t.bg.rowAlt : t.bg.row }}>
                  <td style={{ padding: "10px 14px", fontWeight: 700, color: t.text.primary }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", background: COLORS[i%COLORS.length]+"22", border: `1px solid ${COLORS[i%COLORS.length]}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: COLORS[i%COLORS.length] }}>
                        {(p.sales_rep||"?")[0].toUpperCase()}
                      </div>
                      {p.sales_rep||"—"}
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, color: regionColor, background: `${regionColor}18`, border: `1px solid ${regionColor}33` }}>{regionLabel}</span>
                  </td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: t.text.secondary }}>{levelLabels[p.sales_level]||p.sales_level||"—"}</td>
                  <td style={{ padding: "10px 14px", fontSize: 13, color: t.accent.blue, fontWeight: 700 }}>{p.total_orders}</td>
                  <td style={{ padding: "10px 14px", fontSize: 13, color: "#22c55e" }}>{p.completed_orders}</td>
                  <td style={{ padding: "10px 14px", fontSize: 13, color: "#00ff9d", fontWeight: 700 }}>{moneyS(p.total_amount)}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: t.text.secondary }}>{moneyS(p.avg_order_value)}</td>
                  <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.secondary }}>{p.unique_customers}</td>
                  <td style={{ padding: "10px 14px" }}>
                    {pct !== null ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ flex: 1, height: 6, background: t.bg.tag, borderRadius: 3, overflow: "hidden", minWidth: 60 }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: pctColor, borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: pctColor, minWidth: 32 }}>%{pct}</span>
                      </div>
                    ) : <span style={{ fontSize: 11, color: t.text.muted }}>{r.salesNoTarget}</span>}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <button onClick={() => fetchPerson(p.sales_rep)} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 11 }}>{r.salesDetail}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── EN ÇOK SATAN ÜRÜNLER ── */}
      <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, fontSize: 14, fontWeight: 700, color: t.text.primary }}>
          {r.salesTopProducts}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: t.bg.theadRow }}>
              {[r.salesColProductCode,r.salesColProductName,r.salesColOrderCount,r.salesColTotalQty,r.salesColRevenue].map(h => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.top_products||[]).length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: t.text.placeholder }}>{r.salesPerfNoData}</td></tr>
            ) : (data.top_products||[]).map((p, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i%2===0 ? t.bg.rowAlt : t.bg.row }}>
                <td style={{ padding: "10px 14px", fontSize: 12, fontWeight: 700, color: t.accent.blue }}>{p.product_code}</td>
                <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.primary }}>{p.product_name}</td>
                <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.secondary }}>{p.order_count}</td>
                <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.secondary }}>{Number(p.total_qty||0).toLocaleString(r.numberLocale || "tr-TR")} {r.salesUnitPieces}</td>
                <td style={{ padding: "10px 14px", fontSize: 13, color: "#00ff9d", fontWeight: 700 }}>{moneyS(p.total_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── TOPLAM SATIR ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
        {[
          { label:r.salesDomesticTotal, value: moneyS(domRow.total_amount), color:"#10b981", icon:"🏠", orders: domRow.total_orders||0, region:"domestic" },
          { label:r.salesForeignTotal, value: moneyS(forRow.total_amount), color:"#8b5cf6", icon:"✈️", orders: forRow.total_orders||0, region:"foreign" },
          { label:r.salesGrandTotal, value: moneyS(data.summary?.total_amount), color:"#00ff9d", icon:"💰", orders: data.summary?.total_orders||0, region:"all" },
        ].filter(item => 
          data.user_region === "all" || item.region === data.user_region || item.region === "all"
        ).map(item => (
          <div key={item.label} style={{ background: t.bg.card, border: `2px solid ${item.color}33`, borderRadius: 14, padding: "20px 24px", textAlign: "center", boxShadow: t.shadow.card }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{item.icon}</div>
            <div style={{ fontSize: 12, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>{item.label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: item.color, marginBottom: 4 }}>{item.value}</div>
            <div style={{ fontSize: 12, color: t.text.muted }}>{item.orders} {r.salesOrderSuffix}</div>
          </div>
        ))}
      </div>

      {/* Personel detay modal */}
      {selectedPerson && personDetail && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => setSelectedPerson(null)}>
          <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 600, maxHeight: "80vh", overflow: "auto", padding: 24 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text.primary }}>👤 {selectedPerson}</div>
              <button onClick={() => setSelectedPerson(null)} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
              {[
                [r.salesTotalOrders, personDetail.stats?.total_orders, "#60a5fa"],
                [r.salesTotalRevenue, moneyS(personDetail.stats?.total_amount), "#00ff9d"],
                [r.salesColAvgOrder, moneyS(personDetail.stats?.avg_order), "#a78bfa"],
                [r.salesColCompleted, personDetail.stats?.completed, "#22c55e"],
                [r.salesColCustomer, personDetail.stats?.customers, "#f97316"],
              ].map(([label, val, color]) => (
                <div key={label} style={{ background: t.bg.card, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 10, color: t.text.muted, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color }}>{val}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 10 }}>{r.salesRecentOrders}</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: t.bg.theadRow }}>
                  {[r.salesOrderNo,r.salesColCustomer,r.salesAmount,r.salesStatus,r.salesDate].map(h => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(personDetail.orders||[]).map((o, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                    <td style={{ padding: "8px 12px", fontSize: 12, color: t.accent.blue }}>{o.order_no}</td>
                    <td style={{ padding: "8px 12px", fontSize: 12, color: t.text.primary }}>{o.customer_name}</td>
                    <td style={{ padding: "8px 12px", fontSize: 12, color: "#00ff9d" }}>{moneyS(o.total_amount)}</td>
                    <td style={{ padding: "8px 12px", fontSize: 11 }}>
                      <span style={{ padding: "2px 8px", borderRadius: 20, background: o.status==="completed"?"#22c55e22":"#f9731622", color: o.status==="completed"?"#22c55e":"#f97316", fontWeight: 700 }}>{
  o.status === "shipped"
    ? r.salesStatusShipped
    : o.status === "partial_shipped"
      ? r.salesStatusPartialShipped
      : o.status === "completed"
        ? r.salesStatusCompleted
        : o.status
}</span>
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 11, color: t.text.muted }}>{o.created_at ? new Date(o.created_at).toLocaleDateString(r.numberLocale || "tr-TR") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── PERSONEL & İK RAPORU ─────────────────────────────────────────────────
const PersonnelReport = ({ t, navigateTo }) => {
  const [employees, setEmployees]   = useState([]);
  const [depts, setDepts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [period, setPeriod]         = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().slice(0,10),
    end:   new Date().toISOString().slice(0,10),
  });
  const [selected, setSelected]     = useState(null);
  const [detail, setDetail]         = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(null);
  const [summary, setSummary]       = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r2 = await fetch(
        `${API}/reports/personnel-summary?start_date=${period.start}&end_date=${period.end}`,
        { headers: headers() }
      );
      if (r2.ok) {
        const summaryList = await r2.json();
        const list = Array.isArray(summaryList) ? summaryList : [];
        setSummary(list);
        setEmployees(list.map(e => ({
          id: e.id,
          name: `${e.name} ${e.surname}`.trim(),
          full_name: `${e.name} ${e.surname}`.trim(),
          department: e.department,
          title: e.role,
          role: e.role,
          status: e.status,
          present_days: e.present_days,
          absent_days: e.absent_days,
          total_overtime: e.total_overtime,
          base_salary: e.base_salary,
        })));
        const uniqueDepts = [...new Set(list.map(e => e.department).filter(Boolean))];
        setDepts(uniqueDepts);
      }
    } catch(e) { console.log(e); }
    setLoading(false);
  }, [period.start, period.end]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openDetail = async (emp) => {
    setSelected(emp);
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/reports/personnel-detail/${emp.id}?start_date=${period.start}&end_date=${period.end}`, { headers: headers() });
      if (res.ok) setDetail(await res.json());
      else setDetail(null);
    } catch { setDetail(null); }
    setDetailLoading(false);
  };

  const downloadReport = async (format, empId = null) => {
    const key = empId ? `${format}_${empId}` : `${format}_all`;
    setGenLoading(key);
    try {
      const body = {
        start_date: period.start,
        end_date:   period.end,
        format,
        department: deptFilter !== "all" ? deptFilter : null,
        employee_id: empId || null,
      };
      const res = await fetch(`${API}/reports/personnel_all`, {
        method: "POST", headers: headers(), body: JSON.stringify(body),
      });
      if (!res.ok) { alert(pr.reportsGenerationFailed); setGenLoading(null); return; }
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      const name = empId ? `personel_${empId}` : "personel_tumu";
      a.download = `${name}_${period.start}_${period.end}.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch(e) { alert(e.message); }
    setGenLoading(null);
  };

  const filtered = employees.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !q || (e.name||"").toLowerCase().includes(q) || (e.department||"").toLowerCase().includes(q) || (e.title||"").toLowerCase().includes(q);
    const matchDept   = deptFilter === "all" || e.department === deptFilter;
    return matchSearch && matchDept;
  });

  const inputStyle = { padding: "9px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none" };
  const btnStyle   = (color) => ({ padding: "8px 16px", borderRadius: 8, border: `1px solid ${color}44`, background: `${color}18`, color, cursor: "pointer", fontSize: 12, fontWeight: 700 });
  const { t: personnelLang, lang: currentLang } = useLanguage();
  const pr = personnelLang.reports || {};
  const personnelLocale =
    pr.numberLocale || (currentLang === "en" ? "en-US" : "tr-TR");

  const DEPT_COLORS = { "Üretim":"#60a5fa","Satış":"#00ff9d","Bakım":"#fbbf24","Depo":"#f97316","İK":"#e879f9","Kalite":"#34d399","Yönetim":"#a78bfa" };
  const dColor = (d) => DEPT_COLORS[d] || "#94a3b8";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {summary && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
          {[
            [pr.personnelReportTotalPersonnel, summary.total_employees, pr.personnelReportPersonUnit, "#60a5fa"],
            [pr.personnelReportActivePersonnel, summary.active_employees, pr.personnelReportPersonUnit, "#22c55e"],
            [pr.personnelReportTotalOvertime, summary.total_overtime_hours, pr.personnelReportHourUnit, "#fbbf24"],
            [pr.personnelReportTotalAbsence, summary.total_absent_days, pr.personnelReportDayUnit, "#ef4444"],
          ].map(([label,val,unit,color]) => (
            <div key={label} style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:"18px 20px", boxShadow:t.shadow.card }}>
              <div style={{ fontSize:11, color:t.text.muted, fontWeight:700, textTransform:"uppercase", marginBottom:8 }}>{label}</div>
              <div style={{ fontSize:22, fontWeight:800, color }}>{val ?? "—"} <span style={{ fontSize:12, color:t.text.muted }}>{unit}</span></div>
            </div>
          ))}
        </div>
      )}

      <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:20, boxShadow:t.shadow.card }}>
        <div style={{ display:"flex", flexWrap:"wrap", gap:12, alignItems:"center" }}>
          <input placeholder={pr.personnelReportSearchPlaceholder} value={search} onChange={e=>setSearch(e.target.value)} style={{ ...inputStyle, minWidth:260, flex:1 }} />
          <select value={deptFilter} onChange={e=>setDeptFilter(e.target.value)} style={{ ...inputStyle, minWidth:160 }}>
            <option value="all">{pr.personnelReportAllDepartments}</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <input type="date" value={period.start} onChange={e=>setPeriod(p=>({...p,start:e.target.value}))} style={inputStyle} />
          <span style={{ color:t.text.muted, fontSize:13 }}>—</span>
          <input type="date" value={period.end} onChange={e=>setPeriod(p=>({...p,end:e.target.value}))} style={inputStyle} />
          <button onClick={()=>downloadReport("pdf")} disabled={genLoading==="pdf_all"} style={btnStyle("#ef4444")}>
            {genLoading==="pdf_all" ? "⏳..." : "📄 PDF (" + pr.personnelReportAllSuffix + ")"}
          </button>
          <button onClick={()=>downloadReport("excel")} disabled={genLoading==="excel_all"} style={btnStyle("#22c55e")}>
            {genLoading==="excel_all" ? "⏳..." : "📊 Excel (" + pr.personnelReportAllSuffix + ")"}
          </button>
        </div>
      </div>

      <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
        <div style={{ padding:"14px 20px", borderBottom:`1px solid ${t.border.default}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ fontSize:14, fontWeight:700, color:t.text.primary }}>{pr.personnelReportListTitle} ({filtered.length})</div>
          <span style={{ fontSize:12, color:t.text.muted }}>{pr.personnelReportDetailHint}</span>
        </div>
        {loading ? (
          <div style={{ padding:60, textAlign:"center", color:t.text.muted }}>{pr.personnelReportLoading}</div>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ background:t.bg.theadRow }}>
                {[
                  pr.personnelReportColumnPersonnel,
                  pr.personnelReportColumnDepartment,
                  pr.personnelReportColumnTitle,
                  pr.personnelReportColumnStatus,
                  pr.personnelReportColumnHireDate,
                  pr.personnelReportColumnShift,
                  ""
                ].map(h => (
                  <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ padding:40, textAlign:"center", color:t.text.placeholder }}>{pr.personnelReportNotFound}</td></tr>
              ) : filtered.map((emp, i) => {
                const dc = dColor(emp.department);
                return (
                  <tr key={emp.id || i} style={{ borderTop:`1px solid ${t.border.subtle}`, background: i%2===0 ? t.bg.rowAlt : t.bg.row }}>
                    <td style={{ padding:"10px 14px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:34, height:34, borderRadius:"50%", background:`${dc}22`, border:`1px solid ${dc}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, fontWeight:800, color:dc }}>
                          {(emp.name||"?")[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:t.text.primary }}>{emp.name}</div>
                          <div style={{ fontSize:11, color:t.text.muted }}>{emp.employee_no || emp.id}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:"10px 14px" }}>
                      <span style={{ fontSize:11, padding:"2px 10px", borderRadius:20, background:`${dc}18`, color:dc, fontWeight:700, border:`1px solid ${dc}33` }}>{emp.department || "—"}</span>
                    </td>
                    <td style={{ padding:"10px 14px", fontSize:13, color:t.text.secondary }}>{emp.title || emp.position || "—"}</td>
                    <td style={{ padding:"10px 14px" }}>
                      <span style={{ fontSize:11, padding:"2px 8px", borderRadius:20, fontWeight:700,
                        background: emp.status==="active" ? "#22c55e18" : "#f9731618",
                        color: emp.status==="active" ? "#22c55e" : "#f97316",
                        border: `1px solid ${emp.status==="active" ? "#22c55e33" : "#f9731633"}` }}>
                        {emp.status==="active" ? pr.personnelReportActive : pr.personnelReportPassive}
                      </span>
                    </td>
                    <td style={{ padding:"10px 14px", fontSize:12, color:t.text.muted }}>
                      {emp.hire_date ? new Date(emp.hire_date).toLocaleDateString(personnelLocale) : "—"}
                    </td>
                    <td style={{ padding:"10px 14px", fontSize:12, color:t.text.secondary }}>{emp.shift || emp.shift_type || "—"}</td>
                    <td style={{ padding:"10px 14px" }}>
                      <button onClick={()=>openDetail(emp)} style={{ padding:"5px 12px", borderRadius:6, border:`1px solid ${t.border.input}`, background:t.bg.toggle, color:t.text.secondary, cursor:"pointer", fontSize:11, fontWeight:600 }}>{pr.personnelReportDetail}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div style={{ position:"fixed", inset:0, zIndex:1000, background:t.bg.overlay, backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          onClick={()=>{ setSelected(null); setDetail(null); }}>
          <div style={{ background:t.bg.modal, border:`1px solid ${t.border.default}`, borderRadius:20, width:"100%", maxWidth:680, maxHeight:"85vh", overflow:"auto", display:"flex", flexDirection:"column" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"20px 28px", background:`linear-gradient(135deg, ${dColor(selected.department)}22, ${dColor(selected.department)}08)`,
              borderBottom:`1px solid ${dColor(selected.department)}33`, display:"flex", justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, zIndex:1 }}>
              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                <div style={{ width:48, height:48, borderRadius:"50%", background:`${dColor(selected.department)}22`, border:`2px solid ${dColor(selected.department)}66`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, fontWeight:800, color:dColor(selected.department) }}>
                  {(selected.name||"?")[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize:18, fontWeight:800, color:t.text.primary }}>{selected.name}</div>
                  <div style={{ fontSize:12, color:t.text.muted }}>{selected.title || selected.position || "—"} · {selected.department || "—"}</div>
                </div>
              </div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <button onClick={()=>downloadReport("pdf", selected.id)} disabled={genLoading===`pdf_${selected.id}`}
                  style={{ padding:"7px 14px", borderRadius:8, border:"1px solid #ef444444", background:"#ef444418", color:"#ef4444", cursor:"pointer", fontSize:12, fontWeight:700 }}>
                  {genLoading===`pdf_${selected.id}` ? "⏳..." : "📄 PDF"}
                </button>
                <button onClick={()=>downloadReport("excel", selected.id)} disabled={genLoading===`excel_${selected.id}`}
                  style={{ padding:"7px 14px", borderRadius:8, border:"1px solid #22c55e44", background:"#22c55e18", color:"#22c55e", cursor:"pointer", fontSize:12, fontWeight:700 }}>
                  {genLoading===`excel_${selected.id}` ? "⏳..." : "📊 Excel"}
                </button>
                <button onClick={()=>{ setSelected(null); setDetail(null); }} style={{ background:"none", border:"none", color:t.text.muted, cursor:"pointer", fontSize:22, marginLeft:4 }}>✕</button>
              </div>
            </div>
            <div style={{ padding:"24px 28px", display:"flex", flexDirection:"column", gap:20 }}>
              {detailLoading ? (
                <div style={{ padding:60, textAlign:"center", color:t.text.muted }}>{pr.personnelReportLoading}</div>
              ) : detail ? (
                <>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
                    {[
                      [pr.personnelReportTotalWork, `${detail.attendance?.total_work_days ?? "—"} ${pr.personnelReportDayUnit}`, "#60a5fa"],
                      [pr.personnelReportAbsence, `${detail.attendance?.absent_days ?? "—"} ${pr.personnelReportDayUnit}`, "#ef4444"],
                      [pr.personnelReportLeaveDays, `${detail.attendance?.leave_days ?? "—"} ${pr.personnelReportDayUnit}`, "#fbbf24"],
                      [pr.personnelReportOvertimeHours, `${detail.attendance?.overtime_hours ?? "—"} ${pr.personnelReportHourUnit}`, "#a78bfa"],
                      [pr.personnelReportGrossSalary, `${Number(detail.payroll?.gross_salary||0).toLocaleString(personnelLocale)} ${getCurrencySymbol()}`, "#22c55e"],
                      [pr.personnelReportNetSalary, `${Number(detail.payroll?.net_salary||0).toLocaleString(personnelLocale)} ${getCurrencySymbol()}`, "#00ff9d"],
                    ].map(([label,val,color]) => (
                      <div key={label} style={{ background:t.bg.toggle, border:`1px solid ${t.border.subtle}`, borderRadius:10, padding:"12px 14px" }}>
                        <div style={{ fontSize:10, color:t.text.muted, textTransform:"uppercase", fontWeight:700, marginBottom:4 }}>{label}</div>
                        <div style={{ fontSize:18, fontWeight:800, color }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  {detail.payroll && (
                    <div style={{ background:t.bg.toggle, border:`1px solid ${t.border.subtle}`, borderRadius:12, padding:"16px 18px" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:t.text.primary, marginBottom:12 }}>{pr.personnelReportPayrollBreakdown}</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {[
                          [pr.personnelReportGrossSalary, detail.payroll.gross_salary, "#22c55e", false],
                          [pr.personnelReportSgkEmployee, detail.payroll.sgk_employee, "#ef4444", true],
                          [pr.personnelReportIncomeTax, detail.payroll.income_tax, "#ef4444", true],
                          [pr.personnelReportStampTax, detail.payroll.stamp_tax, "#f97316", true],
                          [pr.personnelReportUnemploymentInsurance, detail.payroll.unemployment, "#f97316", true],
                          [pr.personnelReportNetSalary, detail.payroll.net_salary, "#00ff9d", false],
                        ].map(([label, val, color, deduct]) => (
                          <div key={label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingBottom:6, borderBottom:`1px solid ${t.border.subtle}` }}>
                            <span style={{ fontSize:13, color:t.text.secondary }}>{deduct ? "  ↳ " : ""}{label}</span>
                            <span style={{ fontSize:13, fontWeight:700, color }}>{deduct ? "- " : ""}{Number(val||0).toLocaleString(personnelLocale)} {getCurrencySymbol()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {detail.attendance_records && detail.attendance_records.length > 0 && (
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:t.text.primary, marginBottom:10 }}>{pr.personnelReportAttendanceTitle}</div>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                        <thead>
                          <tr style={{ background:t.bg.theadRow }}>
                            {[
                              pr.personnelReportDate,
                              pr.personnelReportCheckIn,
                              pr.personnelReportCheckOut,
                              pr.personnelReportAttendanceStatus,
                              pr.personnelReportOvertime
                            ].map(h=>(
                              <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:t.text.label, textTransform:"uppercase" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {detail.attendance_records.slice(0,10).map((r,i)=>(
                            <tr key={i} style={{ borderTop:`1px solid ${t.border.subtle}`, background: i%2===0 ? t.bg.rowAlt : t.bg.row }}>
                              <td style={{ padding:"7px 12px", color:t.text.primary }}>{r.date ? new Date(r.date).toLocaleDateString(personnelLocale) : "—"}</td>
                              <td style={{ padding:"7px 12px", color:t.text.secondary }}>{r.check_in || "—"}</td>
                              <td style={{ padding:"7px 12px", color:t.text.secondary }}>{r.check_out || "—"}</td>
                              <td style={{ padding:"7px 12px" }}>
                                <span style={{ fontSize:10, padding:"2px 7px", borderRadius:20, fontWeight:700,
                                  background: r.status==="present"?"#22c55e18":r.status==="absent"?"#ef444418":"#fbbf2418",
                                  color: r.status==="present"?"#22c55e":r.status==="absent"?"#ef4444":"#fbbf24" }}>
                                  {r.status==="present"
                                    ? pr.personnelReportPresent
                                    : r.status==="absent"
                                      ? pr.personnelReportAbsent
                                      : r.status==="leave"
                                        ? pr.personnelReportLeave
                                        : "—"}
                                </span>
                              </td>
                              <td style={{ padding:"7px 12px", color:"#a78bfa", fontWeight:700 }}>{r.overtime_hours ? `+${r.overtime_hours} ${pr.personnelReportHourUnit}` : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding:40, textAlign:"center", color:t.text.placeholder }}>
                  {pr.personnelReportNoDetail}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── P&L RAPORU ──────────────────────────────────────────────────────────
const PLReport = ({ t }) => {
  
  const { t: plLang, lang: plCurrentLang } = useLanguage();
  const r = plLang.reports || {};
  const plLocale = plCurrentLang === "en" ? "en-US" : "tr-TR";
const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/dashboard/pl-summary`, { headers: headers() })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.text.muted }}>{r.loading}</div>;
  if (!data) return <div style={{ padding: 40, textAlign: "center", color: t.text.muted }}>{r.reportsDataNotFound}</div>;

  const moneyPL = v => `${Number(v || 0).toLocaleString(plLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${getCurrencySymbol()}`;
  const hasBekleyen = (data.toplam_fire_bekleyen || 0) > 0;
  const ic  = data.satis_ic  || {};
  const dis = data.satis_dis || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {hasBekleyen && (
        <div style={{ padding: "12px 18px", background: "#fbbf2415", border: "1px solid #fbbf2444", borderRadius: 12, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 20 }}>⏳</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fbbf24" }}>{r.plPendingScrapDecision}</div>
            <div style={{ fontSize: 12, color: t.text.muted, marginTop: 2 }}>
              {r.plPendingScrapMessage
  .replace("{amount}", moneyPL(data.toplam_fire_bekleyen))}
            </div>
          </div>
        </div>
      )}

      {/* ── SATIŞ AYIRIMI ── */}
      <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, fontSize: 14, fontWeight: 700, color: t.text.primary }}>
          {r.plSalesSplit30}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0 }}>
          {/* Yurt İçi */}
          <div style={{ padding: "20px 24px", borderRight: `1px solid ${t.border.subtle}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#10b981", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
              {r.plDomesticSales}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#10b981", marginBottom: 8 }}>{moneyPL(ic.ciro)}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 12, color: t.text.muted }}>📋 {ic.siparis || 0} {r.plOrderUnit}</div>
              <div style={{ fontSize: 12, color: t.text.muted }}>👥 {ic.musteri || 0} {r.plCustomerUnit}</div>
              <div style={{ fontSize: 12, color: t.text.muted }}>📦 {(ic.adet || 0).toLocaleString(plLocale)} {r.plPieceUnit}</div>
            </div>
            <div style={{ marginTop: 10, height: 4, borderRadius: 4, background: t.bg.toggle, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${ic.oran || 0}%`, background: "#10b981", borderRadius: 4 }} />
            </div>
            <div style={{ fontSize: 11, color: t.text.muted, marginTop: 4 }}>{r.plRevenueShare}: %{ic.oran || 0}</div>
          </div>

          {/* Yurt Dışı */}
          <div style={{ padding: "20px 24px", borderRight: `1px solid ${t.border.subtle}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#8b5cf6", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
              {r.plForeignSales}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#8b5cf6", marginBottom: 8 }}>{moneyPL(dis.ciro)}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 12, color: t.text.muted }}>📋 {dis.siparis || 0} {r.plOrderUnit}</div>
              <div style={{ fontSize: 12, color: t.text.muted }}>👥 {dis.musteri || 0} {r.plCustomerUnit}</div>
              <div style={{ fontSize: 12, color: t.text.muted }}>📦 {(dis.adet || 0).toLocaleString(plLocale)} {r.plPieceUnit}</div>
            </div>
            <div style={{ marginTop: 10, height: 4, borderRadius: 4, background: t.bg.toggle, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${dis.oran || 0}%`, background: "#8b5cf6", borderRadius: 4 }} />
            </div>
            <div style={{ fontSize: 11, color: t.text.muted, marginTop: 4 }}>{r.plRevenueShare}: %{dis.oran || 0}</div>
          </div>

          {/* Genel Toplam */}
          <div style={{ padding: "20px 24px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.accent.blue, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
              {r.plGrandTotal}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: t.accent.blue, marginBottom: 8 }}>{moneyPL(data.toplam_siparis_ciro)}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ fontSize: 12, color: t.text.muted }}>📋 {(ic.siparis || 0) + (dis.siparis || 0)} {r.plTotalOrderUnit}</div>
              <div style={{ fontSize: 12, color: t.text.muted }}>👥 {(ic.musteri || 0) + (dis.musteri || 0)} {r.plTotalCustomerUnit}</div>
              <div style={{ fontSize: 12, color: t.text.muted }}>📦 {((ic.adet || 0) + (dis.adet || 0)).toLocaleString(plLocale)} {r.plTotalPieceUnit}</div>
            </div>
            <div style={{ marginTop: 10, display: "flex", gap: 4, height: 4, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${ic.oran || 0}%`, background: "#10b981" }} />
              <div style={{ height: "100%", width: `${dis.oran || 0}%`, background: "#8b5cf6" }} />
            </div>
            <div style={{ fontSize: 11, color: t.text.muted, marginTop: 4 }}>🟢 {r.plDomesticLegend} %{ic.oran || 0} · 🟣 {r.plForeignLegend} %{dis.oran || 0}</div>
          </div>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12 }}>
        {[
          { label: r.plSalesRevenue,     value: data.toplam_satis,        color: "#22c55e" },
          { label: r.plProductionCost,  value: data.toplam_maliyet,       color: "#f97316" },
          { label: r.plScrapLoss,       value: data.toplam_fire_kaybi,    color: "#ef4444" },
          { label: r.plScrapRevenue,    value: data.toplam_hurda_geliri,  color: "#60a5fa" },
          { label: r.plPendingScrap,   value: data.toplam_hurda_bekleyen, color: "#fbbf24" },
        ].map(card => (
          <div key={card.label} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "18px 20px", boxShadow: t.shadow.card }}>
            <div style={{ fontSize: 11, color: t.text.muted, fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>{card.label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: card.color }}>{moneyPL(card.value)}</div>
          </div>
        ))}
      </div>
      <div style={{ background: t.bg.card, border: `1px solid ${data.net_kar >= 0 ? "#22c55e44" : "#ef444444"}`, borderRadius: 14, padding: "20px 24px", boxShadow: t.shadow.card, textAlign: "center" }}>
        <div style={{ fontSize: 13, color: t.text.muted, marginBottom: 8 }}>{r.plNetProfitLoss30}</div>
        <div style={{ fontSize: 32, fontWeight: 800, color: data.net_kar >= 0 ? "#22c55e" : "#ef4444" }}>{moneyPL(data.net_kar)}</div>
      </div>
      <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${t.border.default}`, fontSize: 14, fontWeight: 700, color: t.text.primary }}>{r.plProductAnalysis}</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: t.bg.theadRow }}>
              {[
                r.plColumnProduct,
                r.plColumnShippedQty,
                r.plSalesRevenue,
                r.plProductionCost,
                r.plColumnConfirmedScrap,
                r.plColumnPendingScrap,
                r.plColumnScrap,
                r.plColumnNet
              ].map(h => (
                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.urunler || []).map((r, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                <td style={{ padding: "10px 12px" }}>
                  <div style={{ fontWeight: 700, color: t.text.primary }}>{r.product_code}</div>
                  <div style={{ fontSize: 11, color: t.text.muted }}>{r.product_name}</div>
                </td>
                <td style={{ padding: "10px 12px", fontSize: 13, color: t.text.secondary }}>{Number(r.sevk_qty || 0).toLocaleString(plLocale)} {r.plPieceUnit}</td>
                <td style={{ padding: "10px 12px", fontSize: 13, color: "#22c55e", fontWeight: 600 }}>{moneyPL(r.satis_geliri)}</td>
                <td style={{ padding: "10px 12px", fontSize: 13, color: "#f97316" }}>{moneyPL(r.uretim_maliyeti)}</td>
                <td style={{ padding: "10px 12px", fontSize: 13, color: "#ef4444" }}>{r.fire_kaybi > 0 ? moneyPL(r.fire_kaybi) : "—"}</td>
                <td style={{ padding: "10px 12px", fontSize: 13, color: "#fbbf24" }}>{r.fire_bekleyen_maliyet > 0 ? `⏳ ${moneyPL(r.fire_bekleyen_maliyet)}` : "—"}</td>
                <td style={{ padding: "10px 12px", fontSize: 13, color: "#60a5fa" }}>{moneyPL(r.hurda_geliri)}</td>
                <td style={{ padding: "10px 12px", fontSize: 14, fontWeight: 800, color: r.net >= 0 ? "#22c55e" : "#ef4444" }}>{moneyPL(r.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── FIRE RAPORU SEKMESİ ─────────────────────────────────────────────────
const FireReport = ({ t, themeName, refreshKey = 0 }) => {
  const { t: l } = useLanguage();
  const r = l.reports || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const fetchFire = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/reports/fire-summary`, { headers: headers() });
      if (!res.ok) throw new Error(r.fireLoadFailed);
      setData(await res.json());
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [r.fireLoadFailed]);

  useEffect(() => {
    fetchFire();
  }, [fetchFire, refreshKey]);

  if (loading) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"80px 0", gap:12 }}><div style={{ width:28, height:28, border:`2px solid ${t.border.default}`, borderTopColor:"#f97316", borderRadius:"50%", animation:"sf-spin 0.8s linear infinite" }} /><span style={{ color:t.text.muted }}>{r.fireLoading}</span></div>;
  if (error) return <div style={{ padding:"20px", background:t.accent.redBg, border:`1px solid ${t.accent.red}44`, borderRadius:12, color:t.accent.red }}>⚠️ {error}</div>;

  const s = data?.summary || {};
  const fireRate = parseFloat(s.avg_fire_rate || 0);
  const fireColor = fireRate > 10 ? "#ef4444" : fireRate > 5 ? "#f97316" : "#22c55e";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <div style={{ padding:"16px 20px", background:"linear-gradient(135deg,#f9731611,#ef444411)", border:"1px solid #f9731633", borderRadius:12, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:"#f97316" }}>{r.fireAnalysisTitle}</div>
          <div style={{ fontSize:12, color:t.text.muted, marginTop:2 }}>{r.fireAnalysisDesc}</div>
        </div>
        <button onClick={fetchFire} style={{ padding:"8px 14px", borderRadius:8, border:"1px solid #f9731644", background:"#f9731611", color:"#f97316", cursor:"pointer", fontSize:12, fontWeight:600 }}>{r.fireRefresh}</button>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
        <SummaryCard label={r.fireAvgRate} value={fireRate.toFixed(1)} unit="%" icon="🔥" color={fireColor} t={t} />
        <SummaryCard label={r.fireTotalFailed} value={(s.total_failed||0).toLocaleString(r.numberLocale || "tr-TR")} unit={r.fireUnitPieces} icon="❌" color="#f97316" t={t} />
        <SummaryCard label={r.fireTotalProduced} value={(s.total_planned||0).toLocaleString(r.numberLocale || "tr-TR")} unit={r.fireUnitPieces} icon="🏭" color={t.accent.blue} t={t} />
        <SummaryCard label={r.fireAffectedProducts} value={s.affected_products||0} unit={r.fireUnitTypes} icon="📦" color={t.accent.purple} t={t} />
      </div>
      <div style={{ display:"flex", gap:4, background:t.bg.toggle, border:`1px solid ${t.border.input}`, borderRadius:10, padding:4, width:"fit-content" }}>
        {[
          ["overview", r.fireTabOverview],
          ["detail", r.fireTabRecords],
          ["trend", r.fireTabTrend],
        ].map(([v,label]) => (
          <button key={v} onClick={() => setActiveTab(v)} style={{ padding:"7px 16px", borderRadius:7, border:"none", cursor:"pointer", background: activeTab===v ? (themeName==="dark"?"rgba(255,255,255,0.12)":"#fff"):"none", color: activeTab===v ? t.text.primary : t.text.muted, fontSize:12, fontWeight:600, boxShadow: activeTab===v ? t.shadow.card:"none" }}>{label}</button>
        ))}
      </div>
      {activeTab === "overview" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
            <div style={{ padding:"14px 18px", borderBottom:`1px solid ${t.border.default}`, fontSize:13, fontWeight:700, color:t.text.primary }}>{r.fireByProduct}</div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ background:t.bg.theadRow }}>{[
                r.fireColProductCode,
                r.fireColProductName,
                r.fireColPlanned,
                r.fireColScrap,
                r.fireColRate,
              ].map(h=><th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:t.text.label, textTransform:"uppercase" }}>{h}</th>)}</tr></thead>
              <tbody>
                {(data?.by_product||[]).length===0 ? <tr><td colSpan={5} style={{ padding:32, textAlign:"center", color:t.text.placeholder }}>{r.fireNoData}</td></tr>
                : (data?.by_product||[]).map((row,i) => { const rate=parseFloat(row.fire_rate||0); const rc=rate>10?"#ef4444":rate>5?"#f97316":"#22c55e"; return (
                  <tr key={i} style={{ borderTop:`1px solid ${t.border.subtle}`, background:i%2===0?t.bg.rowAlt:t.bg.row }}>
                    <td style={{ padding:"10px 12px", fontSize:12, fontWeight:700, color:t.text.primary }}>{row.product_code}</td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:t.text.secondary, maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.product_name||"—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:t.text.secondary }}>{(row.planned||0).toLocaleString(r.numberLocale || "tr-TR")}</td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:"#f97316", fontWeight:700 }}>{(row.failed||0).toLocaleString(r.numberLocale || "tr-TR")}</td>
                    <td style={{ padding:"10px 12px" }}><div style={{ display:"flex", alignItems:"center", gap:8 }}><div style={{ flex:1, height:6, background:t.bg.tag, borderRadius:3, overflow:"hidden" }}><div style={{ width:`${Math.min(rate,100)}%`, height:"100%", background:rc, borderRadius:3 }}/></div><span style={{ fontSize:11, fontWeight:700, color:rc, minWidth:36 }}>%{rate.toFixed(1)}</span></div></td>
                  </tr>);})}
              </tbody>
            </table>
          </div>
          <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
            <div style={{ padding:"14px 18px", borderBottom:`1px solid ${t.border.default}`, fontSize:13, fontWeight:700, color:t.text.primary }}>{r.fireByCustomer}</div>
            <table style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead><tr style={{ background:t.bg.theadRow }}>{[
                r.fireColCustomer,
                r.fireColPlanned,
                r.fireColScrap,
                r.fireColRate,
              ].map(h=><th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:t.text.label, textTransform:"uppercase" }}>{h}</th>)}</tr></thead>
              <tbody>
                {(data?.by_customer||[]).length===0 ? <tr><td colSpan={4} style={{ padding:32, textAlign:"center", color:t.text.placeholder }}>{r.fireNoData}</td></tr>
                : (data?.by_customer||[]).map((row,i) => { const rate=parseFloat(row.fire_rate||0); const rc=rate>10?"#ef4444":rate>5?"#f97316":"#22c55e"; return (
                  <tr key={i} style={{ borderTop:`1px solid ${t.border.subtle}`, background:i%2===0?t.bg.rowAlt:t.bg.row }}>
                    <td style={{ padding:"10px 12px", fontSize:12, fontWeight:600, color:t.text.primary }}>{row.customer}</td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:t.text.secondary }}>{(row.planned||0).toLocaleString(r.numberLocale || "tr-TR")}</td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:"#f97316", fontWeight:700 }}>{(row.failed||0).toLocaleString(r.numberLocale || "tr-TR")}</td>
                    <td style={{ padding:"10px 12px" }}><span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:20, color:rc, background:`${rc}18`, border:`1px solid ${rc}44` }}>%{rate.toFixed(1)}</span></td>
                  </tr>);})}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {activeTab === "detail" && (
        <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
          <div style={{ padding:"14px 18px", borderBottom:`1px solid ${t.border.default}`, fontSize:13, fontWeight:700, color:t.text.primary }}>{r.fireRecent20Title}</div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", minWidth:900 }}>
              <thead><tr style={{ background:t.bg.theadRow }}>{[
                r.fireColWorkOrder,
                r.fireColOrder,
                r.fireColProductCode,
                r.fireColProductName,
                r.fireColCustomer,
                r.fireColPlanned,
                r.fireColPassed,
                r.fireColScrap,
                r.fireColRate,
                r.fireColDate,
                r.fireColCreatedBy
              ].map(h=><th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:t.text.label, textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>)}</tr></thead>
              <tbody>
                {(data?.recent||[]).length===0 ? <tr><td colSpan={11} style={{ padding:40, textAlign:"center", color:t.text.placeholder }}>{r.fireNoRecords}</td></tr>
                : (data?.recent||[]).map((row,i) => { const rate=parseFloat(row.fire_rate||0); const rc=rate>10?"#ef4444":rate>5?"#f97316":"#22c55e"; return (
                  <tr key={row.id} style={{ borderTop:`1px solid ${t.border.subtle}`, background:i%2===0?t.bg.rowAlt:t.bg.row }}>
                    <td style={{ padding:"10px 12px", fontSize:11, color:t.text.muted }}>{row.work_order_id?`IE-${row.work_order_id}`:"—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:11, color:t.accent.blue }}>{row.order_no||"—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:12, fontWeight:700, color:t.text.primary }}>{row.product_code}</td>
                    <td style={{ padding:"10px 12px", fontSize:11, color:t.text.secondary, maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.product_name||"—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:11, color:t.text.secondary }}>{row.customer||"—"}</td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:t.text.secondary, textAlign:"right" }}>{row.planned_qty||0}</td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:"#22c55e", fontWeight:700, textAlign:"right" }}>{row.passed_qty||0}</td>
                    <td style={{ padding:"10px 12px", fontSize:12, color:"#f97316", fontWeight:700, textAlign:"right" }}>{row.failed_qty||0}</td>
                    <td style={{ padding:"10px 12px" }}><span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:20, color:rc, background:`${rc}18`, border:`1px solid ${rc}44` }}>%{rate.toFixed(1)}</span></td>
                    <td style={{ padding:"10px 12px", fontSize:11, color:t.text.muted, whiteSpace:"nowrap" }}>{fmtDt(row.created_at)}</td>
                    <td style={{ padding:"10px 12px", fontSize:11, color:t.text.muted }}>{row.created_by||"—"}</td>
                  </tr>);})}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {activeTab === "trend" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
            <div style={{ fontSize:13, fontWeight:700, color:t.text.primary, marginBottom:16 }}>{r.fireDailyQty}</div>
            {(data?.trend||[]).length>0 ? <ResponsiveContainer width="100%" height={220}><BarChart data={data.trend}><CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid}/><XAxis dataKey="date" stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}} tickFormatter={v=>v?.slice(5)}/><YAxis stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}}/><Tooltip contentStyle={{background:t.chart.tooltip.bg,border:`1px solid ${t.chart.tooltip.border}`,borderRadius:8,color:t.text.primary}}/><Bar dataKey="failed" fill="#f97316" radius={[3,3,0,0]} name={r.fireQty}/></BarChart></ResponsiveContainer>
            : <div style={{ textAlign:"center", color:t.text.placeholder, padding:"60px 0" }}>{r.fireNoData}</div>}
          </div>
          <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
            <div style={{ fontSize:13, fontWeight:700, color:t.text.primary, marginBottom:16 }}>{r.fireDailyRate}</div>
            {(data?.trend||[]).length>0 ? <ResponsiveContainer width="100%" height={220}><LineChart data={data.trend}><CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid}/><XAxis dataKey="date" stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}} tickFormatter={v=>v?.slice(5)}/><YAxis stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}} domain={[0,"auto"]}/><Tooltip contentStyle={{background:t.chart.tooltip.bg,border:`1px solid ${t.chart.tooltip.border}`,borderRadius:8,color:t.text.primary}} formatter={(v)=>[`%${parseFloat(v).toFixed(2)}`, r.fireRateLabel]}/><Line type="monotone" dataKey="fire_rate" stroke="#ef4444" strokeWidth={2.5} dot={{fill:"#ef4444",r:3}} name={r.firePercent}/></LineChart></ResponsiveContainer>
            : <div style={{ textAlign:"center", color:t.text.placeholder, padding:"60px 0" }}>{r.fireNoData}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── RAPOR OLUŞTURMA MODAL ────────────────────────────────────────────────
const GenerateReportModal = ({ reportType, onClose, onGenerated, t }) => {
  const { t: l } = useLanguage();
  const r = l.reports || {};
  const [form, setForm] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    end_date: new Date().toISOString().slice(0, 10),
    format: "pdf",
    include_charts: true,
    include_details: true,
    send_email: false,
    email: "",
    customer_type: "all",
    scope: "all",
    scope_value: "",
    selected_emp_id: null,
  });
  const [loading, setLoading]        = useState(false);
  const [error, setError]            = useState("");
  const [empSuggestions, setEmpSugg] = useState([]);
  const [showSugg, setShowSugg]      = useState(false);
  const [deptList, setDeptList]      = useState([]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (reportType.id !== "personnel_all") return;
    fetch(`${API}/hr/employees?limit=200`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        const list = Array.isArray(d) ? d : d.items || [];
        setDeptList([...new Set(list.map(e => e.department).filter(Boolean))]);
      }).catch(() => {});
  }, [reportType.id]);

  const searchEmp = async (q) => {
    set("scope_value", q); set("selected_emp_id", null);
    if (q.length < 2) { setEmpSugg([]); setShowSugg(false); return; }
    try {
      const res = await fetch(`${API}/hr/employees?search=${encodeURIComponent(q)}&limit=10`, { headers: headers() });
      if (!res.ok) return;
      const d = await res.json();
      const list = Array.isArray(d) ? d : d.items || [];
      setEmpSugg(list); setShowSugg(list.length > 0);
    } catch { setEmpSugg([]); }
  };

  const selectEmp = (emp) => {
    set("scope_value", `${emp.name} ${emp.surname || ""}`.trim());
    set("selected_emp_id", emp.id);
    setShowSugg(false); setEmpSugg([]);
  };

  const handleGenerate = async () => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}${reportType.endpoint}`, {
        method: "POST", headers: headers(), body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || r.reportCreateFailed);
      if (form.format === "pdf" || form.format === "excel") {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${reportType.id}_raporu_${form.start_date}_${form.end_date}.${form.format === "excel" ? "xlsx" : "pdf"}`;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      onGenerated();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inputStyle = { width:"100%", padding:"9px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:1000, background:t.bg.overlay, backdropFilter:"blur(6px)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }} onClick={onClose}>
      <div style={{ background:t.bg.modal, border:`1px solid ${t.border.default}`, borderRadius:20, width:"100%", maxWidth:520, overflow:"hidden", display:"flex", flexDirection:"column", boxShadow:t.shadow.modal }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"20px 28px", background:`linear-gradient(135deg,${reportType.color}22,${reportType.color}08)`, borderBottom:`1px solid ${reportType.color}33`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:17, fontWeight:700, color:t.text.primary }}>{reportType.icon} {reportType.label}</div>
            <div style={{ fontSize:12, color:t.text.muted, marginTop:2 }}>{reportType.desc}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:t.text.muted, cursor:"pointer", fontSize:20 }}>✕</button>
        </div>
        <div style={{ padding:28, display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {[
              {label:r.startDateLabel,k:"start_date"},
              {label:r.endDateLabel,k:"end_date"},
            ].map(f=>(
              <div key={f.k}>
                <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>{f.label}</label>
                <input type="date" value={form[f.k]} onChange={e=>set(f.k,e.target.value)} style={inputStyle}/>
              </div>
            ))}
          </div>
          {reportType.id==="sales_personnel" && (
            <div>
              <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:8 }}>{r.customerType}</label>
              <div style={{ display:"flex", gap:8 }}>
                {[
                  ["all", r.customerAll],
                  ["domestic", r.customerDomestic],
                  ["foreign", r.customerForeign],
                ].map(([v,label])=>(
                  <button key={v} onClick={()=>set("customer_type",v)} style={{ flex:1, padding:"9px 0", borderRadius:8, cursor:"pointer", border:`2px solid ${form.customer_type===v?"#a78bfa":t.border.input}`, background:form.customer_type===v?"#a78bfa18":t.bg.input, color:form.customer_type===v?"#a78bfa":t.text.muted, fontSize:12, fontWeight:700 }}>{label}</button>
                ))}
              </div>
            </div>
          )}
          {reportType.id==="personnel_all" && (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:8 }}>{r.reportScope}</label>
                <div style={{ display:"flex", gap:8 }}>
                  {[
                    ["all", r.scopeAllPersonnel],
                    ["dept", r.scopeDepartment],
                    ["single", r.scopeSinglePersonnel],
                  ].map(([v,label])=>(
                    <button key={v} onClick={()=>set("scope",v)} style={{ flex:1, padding:"9px 0", borderRadius:8, cursor:"pointer", border:`2px solid ${(form.scope||"all")===v?"#fb923c":t.border.input}`, background:(form.scope||"all")===v?"#fb923c18":t.bg.input, color:(form.scope||"all")===v?"#fb923c":t.text.muted, fontSize:11, fontWeight:700 }}>{label}</button>
                  ))}
                </div>
              </div>
              {(form.scope==="dept"||form.scope==="single") && (
                <div style={{ position:"relative" }}>
                  <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:6 }}>{form.scope==="dept" ? r.department : r.employeeName}</label>
                  {form.scope==="dept" ? (
                    <select value={form.scope_value||""} onChange={e=>set("scope_value",e.target.value)} style={{ width:"100%", padding:"9px 12px", background:t.bg.input, border:`1px solid ${t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" }}>
                      <option value="">{r.selectDepartment}</option>
                      {deptList.map(d=><option key={d} value={d}>{d}</option>)}
                    </select>
                  ) : (
                    <>
                      <input placeholder={r.employeePlaceholder} value={form.scope_value||""} onChange={e=>searchEmp(e.target.value)} onBlur={()=>setTimeout(()=>setShowSugg(false),180)} onFocus={()=>empSuggestions.length>0&&setShowSugg(true)} autoComplete="off" style={{ width:"100%", padding:"9px 12px", background:t.bg.input, border:`1px solid ${form.selected_emp_id?"#22c55e":t.border.input}`, borderRadius:8, color:t.text.primary, fontSize:13, outline:"none", boxSizing:"border-box" }}/>
                      {form.selected_emp_id && <span style={{ position:"absolute", right:10, top:34, fontSize:12, color:"#22c55e" }}>{r.selected}</span>}
                      {showSugg && empSuggestions.length>0 && (
                        <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:999, background:t.bg.modal, border:`1px solid ${t.border.default}`, borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,0.3)", overflow:"hidden", marginTop:2 }}>
                          {empSuggestions.map((emp,i)=>(
                            <div key={emp.id||i} onMouseDown={()=>selectEmp(emp)} style={{ padding:"10px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:10, borderBottom:`1px solid ${t.border.subtle}` }} onMouseEnter={e=>e.currentTarget.style.background=t.bg.toggle} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                              <div style={{ width:28, height:28, borderRadius:"50%", background:"#fb923c22", border:"1px solid #fb923c44", display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:800, color:"#fb923c" }}>{(emp.name||"?")[0].toUpperCase()}</div>
                              <div><div style={{ fontSize:13, fontWeight:600, color:t.text.primary }}>{emp.name} {emp.surname||""}</div><div style={{ fontSize:11, color:t.text.muted }}>{emp.department} · {emp.role||emp.title||""}</div></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          <div>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:8 }}>{r.outputFormat}</label>
            <div style={{ display:"flex", gap:8 }}>
              {[{v:"pdf",label:"📄 PDF",color:"#f87171"},{v:"excel",label:"📊 Excel",color:"#00ff9d"},{v:"view",label:`👁️ ${r.view}`,color:"#60a5fa"}].map(opt=>(
                <button key={opt.v} onClick={()=>set("format",opt.v)} style={{ flex:1, padding:"9px 0", borderRadius:8, cursor:"pointer", border:`2px solid ${form.format===opt.v?opt.color:t.border.input}`, background:form.format===opt.v?`${opt.color}18`:t.bg.input, color:form.format===opt.v?opt.color:t.text.muted, fontSize:12, fontWeight:700 }}>{opt.label}</button>
              ))}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {[
              {k:"include_charts",label:r.includeCharts},
              {k:"include_details",label:r.includeDetails},
              {k:"send_email",label:r.sendEmail},
            ].map(opt=>(
              <label key={opt.k} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
                <div onClick={()=>set(opt.k,!form[opt.k])} style={{ width:20, height:20, borderRadius:5, border:`2px solid ${form[opt.k]?t.accent.blue:t.border.input}`, background:form[opt.k]?t.accent.blue:"none", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0 }}>
                  {form[opt.k]&&<span style={{ color:"#fff", fontSize:13, fontWeight:700 }}>✓</span>}
                </div>
                <span style={{ fontSize:13, color:t.text.secondary }}>{opt.label}</span>
              </label>
            ))}
          </div>
          {form.send_email && <div><label style={{ display:"block", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", marginBottom:6 }}>{r.emailAddress}</label><input type="email" value={form.email} onChange={e=>set("email",e.target.value)} placeholder="ornek@fabrika.com" style={inputStyle}/></div>}
          {error && <div style={{ padding:"10px 14px", background:t.accent.redBg, border:`1px solid ${t.accent.red}44`, borderRadius:8, color:t.accent.red, fontSize:13 }}>⚠️ {error}</div>}
        </div>
        <div style={{ padding:"16px 28px", borderTop:`1px solid ${t.border.default}`, display:"flex", justifyContent:"flex-end", gap:12, background:t.bg.header }}>
          <button onClick={onClose} style={{ padding:"10px 20px", borderRadius:8, border:`1px solid ${t.border.input}`, background:"none", color:t.text.secondary, cursor:"pointer", fontSize:13 }}>{r.cancel}</button>
          <button onClick={handleGenerate} disabled={loading} style={{ padding:"10px 24px", borderRadius:8, border:"none", background:loading?t.bg.tag:`linear-gradient(135deg,${reportType.color},${reportType.color}bb)`, color:"#fff", cursor:loading?"default":"pointer", fontSize:13, fontWeight:700 }}>
            {loading ? r.generating : `${reportType.icon} ${r.generateReport}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function Reports() {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
  const r = l.reports || {};

  const reportTypes = REPORT_TYPES.map((rt) => ({
    ...rt,
    label: r[rt.labelKey] || rt.id,
    desc: r[rt.descKey] || "",
  }));

  const userRole = localStorage.getItem('role') || '';
  const isSalesRole = [
    'dis_ticaret_mudur','dis_ticaret_uzman','dis_ticaret_temsilci',
    'ic_satis_mudur','ic_satis_uzman','ic_satis_temsilci',
  ].includes(userRole);

  const [contentPerms, setContentPerms] = useState(null); // null = henüz yüklenmedi

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const tok = localStorage.getItem('token');
        const r = await fetch(`${API}/content-permissions`, {
          headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' }
        });
        if (r.ok) setContentPerms(await r.json());
        else setContentPerms({});
      } catch { setContentPerms({}); }
    };
    fetchPerms();
  }, []);

  // Sekme görünürlük kontrolü: DB'de false olarak işaretlenmişse gizle
  const isTabVisible = (tabKey) => {
    if (contentPerms === null) return true; // henüz yüklenmedi, hepsini göster
    const reportsPerms = contentPerms['reports'] || {};
    const dbKey = `tab_${tabKey}`;
    return reportsPerms[dbKey] !== false;
  };

  const ALL_TABS = [
    ["dashboard",  r.tabDashboard],
    ["fire",       r.tabScrap],
    ["pl",         r.tabQualityPnl],
    ["sales_perf", r.tabSalesPerformance],
    ["personnel",  r.tabPersonnelReports],
    ["reports",    r.tabGenerateReport],
    ["history",    r.tabHistory],
    ["variance",   r.tabVarianceAnalysis],
  ];

  const visibleTabs = isSalesRole
    ? ALL_TABS.filter(([v]) => v === "sales_perf")
    : ALL_TABS.filter(([v]) => isTabVisible(v));

  const defaultTab = isSalesRole ? "sales_perf" :
    (visibleTabs.find(([v]) => v === (localStorage.getItem("tab_reports") || "dashboard"))?.[0]
      || visibleTabs[0]?.[0] || "dashboard");

  const [mainTab, setMainTab] = useState(defaultTab);
  const [summary, setSummary]       = useState(null);
  const [productionChart, setProductionChart] = useState([]);
  const [qualityChart, setQualityChart]       = useState([]);
  const [costChart, setCostChart]             = useState([]);
  const [history, setHistory]       = useState([]);
  const [fireSummary, setFireSummary] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [r1,r2,r3,r4,r5,r6] = await Promise.all([
        fetch(`${API}/reports/summary`,              { headers: headers() }),
        fetch(`${API}/reports/production-chart?days=30`, { headers: headers() }),
        fetch(`${API}/reports/quality-chart?days=30`,    { headers: headers() }),
        fetch(`${API}/reports/cost-chart?days=30`,       { headers: headers() }),
        fetch(`${API}/reports/history?limit=20`,         { headers: headers() }),
        fetch(`${API}/reports/fire-summary`,             { headers: headers() }),
      ]);
      if (r1.ok) setSummary(await r1.json());
      if (r2.ok) setProductionChart(await r2.json());
      if (r3.ok) setQualityChart(await r3.json());
      if (r4.ok) setCostChart(await r4.json());
      if (r5.ok) { const d = await r5.json(); setHistory(Array.isArray(d)?d:d.items||[]); }
      if (r6.ok) setFireSummary(await r6.json());
    } catch(e) { console.log(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = () => {
    if (mainTab === "fire") {
      setRefreshKey((k) => k + 1);
      return;
    }

    fetchData();
  };

  const PIE_COLORS = [t.accent.blue, t.accent.green, t.accent.yellow, t.accent.red, t.accent.purple];
  const fireRate   = parseFloat(fireSummary?.summary?.avg_fire_rate || 0);
  const fireColor  = fireRate > 10 ? "#ef4444" : fireRate > 5 ? "#f97316" : "#22c55e";

  return (
    <div style={{ minHeight:"100vh", background:t.bg.page, color:t.text.primary, fontFamily:"'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');@keyframes sf-spin{to{transform:rotate(360deg)}}@keyframes sf-pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
      <div style={{ maxWidth:1400, margin:"0 auto", padding:"28px 24px" }}>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
          <div>
            <h1 style={{ fontSize:26, fontWeight:800, color:t.text.primary, margin:0, letterSpacing:"-0.02em" }}>📈 {r.title}</h1>
            <p style={{ color:t.text.muted, margin:"6px 0 0", fontSize:14 }}>{r.subtitle}</p>
          </div>
          <button onClick={handleRefresh} style={{ padding:"10px 16px", borderRadius:10, border:`1px solid ${t.border.input}`, background:t.bg.toggle, color:t.text.secondary, cursor:"pointer", fontSize:13 }}>🔄 {r.refresh}</button>
        </div>

        <div style={{ display:"flex", gap:4, marginBottom:24, background:t.bg.toggle, border:`1px solid ${t.border.input}`, borderRadius:12, padding:4, width:"fit-content" }}>
          {visibleTabs.map(([v, label]) => (
            <button key={v} onClick={() => { setMainTab(v); if (!isSalesRole) localStorage.setItem("tab_reports", v); }} style={{
              padding:"9px 18px", borderRadius:9, border:"none", cursor:"pointer",
              background: mainTab===v ? (themeName==="dark"?"rgba(255,255,255,0.12)":"#fff") : "none",
              color: mainTab===v ? t.text.primary : t.text.muted,
              fontSize:13, fontWeight:600, boxShadow: mainTab===v ? t.shadow.card : "none", transition:"all 0.2s",
            }}>{label}</button>
          ))}
        </div>

        {loading && mainTab==="dashboard" ? (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"80px 0", gap:12 }}>
            <div style={{ width:28, height:28, border:`2px solid ${t.border.default}`, borderTopColor:t.accent.blue, borderRadius:"50%", animation:"sf-spin 0.8s linear infinite" }}/>
            <span style={{ color:t.text.muted }}>{r.loading}</span>
          </div>
        ) : (
          <>
            {mainTab==="dashboard" && (
              <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
                  <SummaryCard label={r.factoryOee} value={summary?.factory_oee??"—"} unit="%" icon="📊" color={summary?.factory_oee>=85?t.accent.green:t.accent.yellow} change={summary?.oee_change} t={t}/>
                  <SummaryCard label={r.totalProduction} value={summary?.total_production?.toLocaleString(getLocale())??"—"} unit={r.unitPieces} icon="🏭" color={t.accent.blue} change={summary?.production_change} t={t}/>
                  <SummaryCard label={r.qualityRate} value={summary?.quality_rate??"—"} unit="%" icon="✅" color={t.accent.green} change={summary?.quality_change} t={t}/>
                  <SummaryCard label={r.energyCost} value={summary?.energy_cost?.toFixed(0)??"—"} unit={getCurrencySymbol()} icon="⚡" color={t.accent.yellow} change={summary?.energy_change} t={t}/>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
                  <SummaryCard label={r.maintenanceCost} value={summary?.maintenance_cost?.toFixed(0)??"—"} unit={getCurrencySymbol()} icon="🔧" color={t.accent.red} change={summary?.maintenance_change} t={t}/>
                  <SummaryCard label={r.stockValue} value={summary?.stock_value?`${(summary.stock_value/1000).toFixed(0)}K`:"—"} unit={getCurrencySymbol()} icon="📦" color={t.accent.purple} t={t}/>
                  <SummaryCard label={r.activeEmployees} value={summary?.active_employees??"—"} unit={r.unitPeople} icon="👥" color={t.accent.blue} t={t}/>
                  <SummaryCard label={r.scrapRate30d} value={fireRate.toFixed(1)} unit="%" icon="🔥" color={fireColor} t={t}/>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16 }}>
                  <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                    <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:20 }}>📈 {r.productionOeeTrend30d}</div>
                    {productionChart.length>0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={productionChart}>
                          <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid}/>
                          <XAxis dataKey="date" stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}} tickFormatter={v=>v?.slice(5)}/>
                          <YAxis yAxisId="left" stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}}/>
                          <YAxis yAxisId="right" orientation="right" stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}} domain={[0,100]}/>
                          <Tooltip contentStyle={{background:t.chart.tooltip.bg,border:`1px solid ${t.chart.tooltip.border}`,borderRadius:8,color:t.text.primary}}/>
                          <Legend/>
                          <Line yAxisId="left" type="monotone" dataKey="production" stroke={t.accent.blue} strokeWidth={2} dot={false} name={r.productionLegend}/>
                          <Line yAxisId="right" type="monotone" dataKey="oee" stroke={t.accent.green} strokeWidth={2} dot={false} name="OEE %"/>
                        </LineChart>
                      </ResponsiveContainer>
                    ) : <div style={{ textAlign:"center", color:t.text.placeholder, padding:"60px 0" }}>{r.noProductionData}</div>}
                  </div>
                  <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                    <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:20 }}>🏭 {r.machineStatusDistribution}</div>
                    {summary?.machine_status_dist ? (
                      <>
                        <ResponsiveContainer width="100%" height={160}>
                          <PieChart>
                            <Pie data={summary.machine_status_dist} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={65} label={false}>
                              {summary.machine_status_dist.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                            </Pie>
                            <Tooltip contentStyle={{background:t.chart.tooltip.bg,border:`1px solid ${t.chart.tooltip.border}`,borderRadius:8,color:t.text.primary}}/>
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                          {summary.machine_status_dist.map((item,i)=>(
                            <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                                <div style={{ width:10, height:10, borderRadius:"50%", background:PIE_COLORS[i%PIE_COLORS.length] }}/>
                                <span style={{ color:t.text.secondary }}>
  {r.machineStatus?.[item.status] || item.status}
</span>
                              </div>
                              <span style={{ fontWeight:700, color:t.text.primary }}>{item.count}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : <div style={{ textAlign:"center", color:t.text.placeholder, padding:"60px 0" }}>{r.noMachineData}</div>}
                  </div>
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                  <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                    <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:20 }}>✅ {r.qualityRateTrend}</div>
                    {qualityChart.length>0 ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={qualityChart}>
                          <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid}/>
                          <XAxis dataKey="date" stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}} tickFormatter={v=>v?.slice(5)}/>
                          <YAxis stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}} domain={[0,100]}/>
                          <Tooltip contentStyle={{background:t.chart.tooltip.bg,border:`1px solid ${t.chart.tooltip.border}`,borderRadius:8,color:t.text.primary}}/>
                          <Bar dataKey="pass_rate" fill={t.accent.green} radius={[3,3,0,0]} name={r.qualityPassLegend}/>
                          <Bar dataKey="fail_rate" fill={t.accent.red} radius={[3,3,0,0]} name={r.qualityFailLegend}/>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <div style={{ textAlign:"center", color:t.text.placeholder, padding:"40px 0" }}>{r.noData}</div>}
                  </div>
                  <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                    <div style={{ fontSize:14, fontWeight:700, color:t.text.primary, marginBottom:20 }}>💰 {r.costTrend}</div>
                    {costChart.length>0 ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <LineChart data={costChart}>
                          <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid}/>
                          <XAxis dataKey="date" stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}} tickFormatter={v=>v?.slice(5)}/>
                          <YAxis stroke={t.chart.axis} tick={{fontSize:10,fill:t.chart.axis}}/>
                          <Tooltip contentStyle={{background:t.chart.tooltip.bg,border:`1px solid ${t.chart.tooltip.border}`,borderRadius:8,color:t.text.primary}}/>
                          <Legend/>
                          <Line type="monotone" dataKey="energy_cost" stroke={t.accent.yellow} strokeWidth={2} dot={false} name={r.energyLegend}/>
                          <Line type="monotone" dataKey="maintenance_cost" stroke={t.accent.red} strokeWidth={2} dot={false} name={r.maintenanceLegend}/>
                          <Line type="monotone" dataKey="production_cost" stroke={t.accent.blue} strokeWidth={2} dot={false} name={r.productionCostLegend}/>
                        </LineChart>
                      </ResponsiveContainer>
                    ) : <div style={{ textAlign:"center", color:t.text.placeholder, padding:"40px 0" }}>{r.noData}</div>}
                  </div>
                </div>
                {fireSummary && (
                  <div style={{ background:t.bg.card, border:"1px solid #f9731633", borderRadius:14, padding:24, boxShadow:t.shadow.card }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                      <div style={{ fontSize:14, fontWeight:700, color:t.text.primary }}>{r.summaryFire30Title}</div>
                      <button onClick={()=>{ setMainTab("fire"); localStorage.setItem("tab_reports","fire"); }} style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #f9731644", background:"#f9731611", color:"#f97316", cursor:"pointer", fontSize:12, fontWeight:600 }}>{r.summaryDetailButton}</button>
                    </div>
                    {(fireSummary.by_product||[]).length===0 ? (
                      <div style={{ textAlign:"center", color:t.text.placeholder, padding:"20px 0" }}>{r.summaryNoFire}</div>
                    ) : (
                      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:10 }}>
                        {(fireSummary.by_product||[]).slice(0,6).map((row,i)=>{ const rate=parseFloat(row.fire_rate||0); const rc=rate>10?"#ef4444":rate>5?"#f97316":"#22c55e"; return (
                          <div key={i} style={{ padding:"12px 14px", background:t.bg.toggle, border:`1px solid ${t.border.subtle}`, borderRadius:10 }}>
                            <div style={{ fontSize:12, fontWeight:700, color:t.text.primary, marginBottom:4 }}>{row.product_code}</div>
                            <div style={{ fontSize:11, color:t.text.muted, marginBottom:8, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.product_name||"—"}</div>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                              <span style={{ fontSize:11, color:t.text.muted }}>{row.failed} {r.summaryScrapUnit}</span>
                              <span style={{ fontSize:12, fontWeight:700, color:rc }}>%{rate.toFixed(1)}</span>
                            </div>
                            <div style={{ marginTop:6, height:4, background:t.bg.tag, borderRadius:2, overflow:"hidden" }}>
                              <div style={{ width:`${Math.min(rate,100)}%`, height:"100%", background:rc, borderRadius:2 }}/>
                            </div>
                          </div>
                        );})}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {mainTab==="fire"       && <FireReport t={t} themeName={themeName} refreshKey={refreshKey}/>}
            {mainTab==="reports"    && (
              <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
                <div style={{ padding:"14px 20px", background:t.accent.blueBg, border:`1px solid ${t.accent.blueBorder}`, borderRadius:12, fontSize:13, color:t.accent.blue }}>
                  {r.selectReportHint}
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:14 }}>
                  {reportTypes.map(rt=>(
                    <div key={rt.id} onClick={()=>setSelectedReport(rt)} style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, padding:20, cursor:"pointer", transition:"all 0.2s", boxShadow:t.shadow.card, position:"relative", overflow:"hidden" }}
                      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.borderColor=`${rt.color}55`;}}
                      onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.borderColor=t.border.card;}}>
                      <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${rt.color},transparent)`, borderRadius:"14px 14px 0 0" }}/>
                      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:12 }}>
                        <div style={{ width:44, height:44, borderRadius:12, background:`${rt.color}18`, border:`1px solid ${rt.color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>{rt.icon}</div>
                        <div>
                          <div style={{ fontSize:15, fontWeight:700, color:t.text.primary }}>{rt.label}</div>
                          <div style={{ fontSize:12, color:t.text.muted, marginTop:2 }}>{rt.desc}</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:6 }}>
                        {["PDF","Excel",r.view].map(f=><span key={f} style={{ fontSize:10, padding:"2px 8px", borderRadius:20, background:t.bg.tag, color:t.text.muted, border:`1px solid ${t.border.subtle}`, fontWeight:600 }}>{f}</span>)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {mainTab==="pl"         && <PLReport t={t}/>}
            {mainTab==="sales_perf" && <SalesPerformance t={t} themeName={themeName}/>}
            {mainTab==="personnel"  && <PersonnelReport t={t}/>}
            {mainTab==="history"    && (
              <div style={{ background:t.bg.card, border:`1px solid ${t.border.card}`, borderRadius:14, overflow:"hidden", boxShadow:t.shadow.card }}>
                <div style={{ padding:"16px 20px", borderBottom:`1px solid ${t.border.default}`, fontSize:14, fontWeight:700, color:t.text.primary }}>{r.historyTitle}</div>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{ background:t.bg.theadRow }}>
                      {[
                        r.historyReportType,
                        r.historyFormat,
                        r.historyDateRange,
                        r.historyCreatedBy,
                        r.historyDate,
                        r.historyDownload
                      ].map(h=>(
                        <th key={h} style={{ padding:"12px 14px", textAlign:"left", fontSize:11, fontWeight:700, color:t.text.label, textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.length===0
                      ? <tr><td colSpan={6} style={{ textAlign:"center", color:t.text.placeholder, padding:40 }}>{r.historyEmpty}</td></tr>
                      : history.map((r,i)=>{ const rt=reportTypes.find(t=>t.id===r.report_type)||reportTypes[0]; return (
                        <tr key={r.id||i} style={{ borderTop:`1px solid ${t.border.subtle}`, background:i%2===0?t.bg.rowAlt:t.bg.row }}>
                          <td style={{ padding:"12px 14px" }}><div style={{ display:"flex", alignItems:"center", gap:8 }}><span style={{ fontSize:16 }}>{rt.icon}</span><span style={{ fontSize:13, fontWeight:600, color:t.text.primary }}>{rt.label}</span></div></td>
                          <td style={{ padding:"12px 14px" }}><span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:20, color:r.format==="pdf"?t.accent.red:t.accent.green, background:r.format==="pdf"?t.accent.redBg:t.accent.greenBg }}>{r.format?.toUpperCase()||"PDF"}</span></td>
                          <td style={{ padding:"12px 14px", fontSize:12, color:t.text.muted }}>{fmt(r.start_date)} — {fmt(r.end_date)}</td>
                          <td style={{ padding:"12px 14px", fontSize:12, color:t.text.muted }}>{r.created_by||"—"}</td>
                          <td style={{ padding:"12px 14px", fontSize:12, color:t.text.muted }}>{fmt(r.created_at)}</td>
                          <td style={{ padding:"12px 14px" }}>{r.file_url&&<a href={`${API}${r.file_url}`} download style={{ padding:"4px 12px", borderRadius:6, fontSize:11, cursor:"pointer", background:t.accent.blueBg, border:`1px solid ${t.accent.blueBorder}`, color:t.accent.blue, textDecoration:"none", fontWeight:600 }}>{r.historyDownloadButton}</a>}</td>
                        </tr>);
                      })}
                  </tbody>
                </table>
              </div>
            )}
            {mainTab==="variance" && <VarianceAnalysisTab t={t} themeName={themeName}/>}
          </>
        )}
      </div>

      {selectedReport && (
        <GenerateReportModal reportType={selectedReport} t={t}
          onClose={()=>setSelectedReport(null)}
          onGenerated={()=>{ setSelectedReport(null); fetchData(); }}/>
      )}
    </div>
  );
}
